import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { extractErr, mediaUrl, storePageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useMerchantAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ExternalLink, Upload, ImageIcon, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFileUpload } from "@/lib/uploadProgress";
import { contactList } from "@/lib/contactFields";

const PAGE_TAB = {
  about: "pages.tabAbout",
  contact: "pages.tabContact",
  shipping: "pages.tabShipping",
  privacy: "pages.tabPrivacy",
  terms: "pages.tabTerms",
  "account-deletion": "pages.tabAccountDeletion",
  faq: "FAQ",
};

function newFaqId() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `faq_${Math.random().toString(36).slice(2, 10)}`;
}

function FaqEditor({ page, patchData }) {
  const d = page.data || {};
  const sections = Array.isArray(d.sections) ? d.sections : [];
  const setSections = (next) => patchData("sections", next);
  const [panel, setPanel] = useState("sections");
  return (
    <div className="space-y-4" data-testid="faq-editor">
      <div className="flex gap-2">
        <button type="button" data-testid="faq-panel-sections" onClick={() => setPanel("sections")} className={`h-9 px-3 rounded-full text-sm font-semibold ${panel === "sections" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>FAQ sections</button>
        <button type="button" data-testid="faq-panel-contacts" onClick={() => setPanel("contacts")} className={`h-9 px-3 rounded-full text-sm font-semibold ${panel === "contacts" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>Contact Data</button>
      </div>
      {panel === "contacts" ? <FaqContacts /> : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Page heading</Label><Input data-testid="faq-heading" className="mt-1" value={d.heading || ""} onChange={(e) => patchData("heading", e.target.value)} placeholder="FAQ's" /></div>
            <div><Label>Intro</Label><Input data-testid="faq-intro" className="mt-1" value={d.intro || ""} onChange={(e) => patchData("intro", e.target.value)} placeholder="A short welcome under the title" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Contact form heading</Label><Input data-testid="faq-form-heading" className="mt-1" value={d.form_heading || ""} onChange={(e) => patchData("form_heading", e.target.value)} placeholder="Didn't find your answer?" /></div>
            <div><Label>Contact form text</Label><Input data-testid="faq-form-subtext" className="mt-1" value={d.form_subtext || ""} onChange={(e) => patchData("form_subtext", e.target.value)} placeholder="Don't hesitate to contact us" /></div>
          </div>
          <div className="flex items-center justify-between">
            <Label>Sections</Label>
            <Button type="button" size="sm" variant="outline" data-testid="faq-add-section" onClick={() => setSections([...sections, { id: newFaqId(), title: "", description: "", items: [] }])}><Plus className="w-3.5 h-3.5 mr-1" /> Add FAQ section</Button>
          </div>
          {sections.length === 0 && <p className="text-sm text-slate-400">Add a section such as Skin Type, then add questions and answers inside it.</p>}
          <div className="space-y-4">
            {sections.map((sec, si) => {
              const items = Array.isArray(sec.items) ? sec.items : [];
              const patchSec = (patch) => {
                const next = sections.map((s, i) => (i === si ? { ...s, ...patch } : s));
                setSections(next);
              };
              return (
                <div key={sec.id || si} className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`faq-section-${si}`}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 grid sm:grid-cols-2 gap-3">
                      <div><Label>Title</Label><Input data-testid={`faq-section-title-${si}`} className="mt-1" value={sec.title || ""} onChange={(e) => patchSec({ title: e.target.value })} placeholder="Skin Type" /></div>
                      <div><Label>Description</Label><Input data-testid={`faq-section-desc-${si}`} className="mt-1" value={sec.description || ""} onChange={(e) => patchSec({ description: e.target.value })} placeholder="Below are some common questions about Skin type" /></div>
                    </div>
                    <button type="button" className="text-red-600 mt-6" data-testid={`faq-section-remove-${si}`} onClick={() => setSections(sections.filter((_, i) => i !== si))} aria-label="Remove section"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-2">
                    {items.map((row, qi) => (
                      <div key={row.id || qi} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2" data-testid={`faq-q-${si}-${qi}`}>
                        <Input data-testid={`faq-question-${si}-${qi}`} value={row.question || ""} placeholder="Question" onChange={(e) => {
                          const next = items.map((it, i) => (i === qi ? { ...it, question: e.target.value } : it));
                          patchSec({ items: next });
                        }} />
                        <Input data-testid={`faq-answer-${si}-${qi}`} value={row.answer || ""} placeholder="Answer" onChange={(e) => {
                          const next = items.map((it, i) => (i === qi ? { ...it, answer: e.target.value } : it));
                          patchSec({ items: next });
                        }} />
                        <button type="button" className="text-red-600 px-1" data-testid={`faq-q-remove-${si}-${qi}`} onClick={() => patchSec({ items: items.filter((_, i) => i !== qi) })} aria-label="Remove question"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" data-testid={`faq-add-q-${si}`} onClick={() => patchSec({ items: [...items, { id: newFaqId(), question: "", answer: "" }] })}><Plus className="w-3.5 h-3.5 mr-1" /> Add question</Button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function FaqContacts() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/merchant/faq-contacts").then(({ data }) => setItems(Array.isArray(data) ? data : [])).catch(() => setItems([]));
  }, []);
  return (
    <div data-testid="faq-contact-data" className="space-y-3">
      <div>
        <div className="font-semibold text-slate-900">Contact Data</div>
        <p className="text-xs text-slate-500 mt-1">Messages sent from the form on the FAQ page.</p>
      </div>
      {items === null ? <p className="text-sm text-slate-400">Loading…</p> : items.length === 0 ? <p className="text-sm text-slate-400" data-testid="faq-contacts-empty">No messages yet.</p> : (
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {items.map((row) => (
            <div key={row.id} className="p-3 text-sm" data-testid="faq-contact-row">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-semibold text-slate-900">{row.name || "—"}</div>
                <div className="text-xs text-slate-400">{row.created_at ? new Date(row.created_at).toLocaleString() : ""}</div>
              </div>
              <div className="text-slate-600">{row.email}</div>
              <p className="mt-1 text-slate-800 whitespace-pre-wrap">{row.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageField({ value, onChange, testid, hint }) {
  const { t } = useTranslation();
  const uploadFile = useFileUpload();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "page");
    setUploading(true);
    try {
      const data = await uploadFile("/merchant/media/upload", fd);
      onChange(data.url);
    } catch (e) { toast.error(extractErr(e)); } finally { setUploading(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-20 h-20 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center flex-shrink-0">
        {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
      </div>
      <div>
        <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer" data-testid={testid}>
          <Upload className="w-4 h-4" /> {uploading ? t("common.uploading") : (value ? t("common.replace") : t("common.upload"))}
          <input type="file" hidden accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
        </label>
        {hint ? <p className="text-[11px] text-slate-400 mt-0.5 leading-snug" data-testid={`${testid}-hint`}>{hint}</p> : null}
      </div>
      {value && <button type="button" className="text-xs text-red-600" onClick={() => onChange("")}>{t("common.remove")}</button>}
    </div>
  );
}

function AboutEditor({ page, patchData }) {
  const { t } = useTranslation();
  const d = page.data || {};
  const values = d.values || [];
  const stats = d.stats || [];
  const setValues = (v) => patchData("values", v);
  const setStats = (v) => patchData("stats", v);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>{t("pages.heading")}</Label><Input data-testid="about-heading" className="mt-1" value={d.heading || ""} onChange={(e) => patchData("heading", e.target.value)} placeholder="Our story" /></div>
        <div><Label>{t("pages.subheading")}</Label><Input data-testid="about-subheading" className="mt-1" value={d.subheading || ""} onChange={(e) => patchData("subheading", e.target.value)} placeholder="A little about who we are" /></div>
      </div>
      <div><Label>{t("pages.coverImage")}</Label><div className="mt-1"><ImageField testid="about-image" hint="Recommended 1600×900 px (16:9). Scales on all screens." value={d.image_url} onChange={(v) => patchData("image_url", v)} /></div></div>
      <div><Label>{t("pages.ourStory")}</Label><Textarea rows={5} data-testid="about-story" className="mt-1" value={d.story || ""} onChange={(e) => patchData("story", e.target.value)} placeholder="Tell customers how your store started and what makes it special…" /></div>
      <div><Label>{t("pages.ourMission")}</Label><Textarea rows={3} data-testid="about-mission" className="mt-1" value={d.mission || ""} onChange={(e) => patchData("mission", e.target.value)} placeholder="What you stand for…" /></div>

      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2"><Label>{t("pages.ourValues")}</Label><Button type="button" size="sm" variant="outline" data-testid="about-add-value" onClick={() => setValues([...values, { title: "", description: "" }])}><Plus className="w-3.5 h-3.5 mr-1" /> {t("common.add")}</Button></div>
        <div className="space-y-2">
          {values.map((v, i) => (
            <div key={i} className="flex gap-2 items-start" data-testid={`about-value-${i}`}>
              <Input className="w-1/3" placeholder="Title" value={v.title || ""} onChange={(e) => { const n = [...values]; n[i] = { ...v, title: e.target.value }; setValues(n); }} />
              <Input className="flex-1" placeholder="Description" value={v.description || ""} onChange={(e) => { const n = [...values]; n[i] = { ...v, description: e.target.value }; setValues(n); }} />
              <button type="button" className="text-slate-400 hover:text-red-600 p-2" onClick={() => setValues(values.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2"><Label>{t("pages.stats")}</Label><Button type="button" size="sm" variant="outline" data-testid="about-add-stat" onClick={() => setStats([...stats, { value: "", label: "" }])}><Plus className="w-3.5 h-3.5 mr-1" /> {t("common.add")}</Button></div>
        <div className="space-y-2">
          {stats.map((s, i) => (
            <div key={i} className="flex gap-2 items-start" data-testid={`about-stat-${i}`}>
              <Input className="w-1/3" placeholder="10k+" value={s.value || ""} onChange={(e) => { const n = [...stats]; n[i] = { ...s, value: e.target.value }; setStats(n); }} />
              <Input className="flex-1" placeholder="Happy customers" value={s.label || ""} onChange={(e) => { const n = [...stats]; n[i] = { ...s, label: e.target.value }; setStats(n); }} />
              <button type="button" className="text-slate-400 hover:text-red-600 p-2" onClick={() => setStats(stats.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ContactEditor({ page, patchData }) {
  const { t } = useTranslation();
  const d = page.data || {};
  const emails = contactList(d, "emails", "email");
  const phones = contactList(d, "phones", "phone");
  const setEmails = (next) => patchData({
    emails: next,
    email: next.map((x) => String(x).trim()).find(Boolean) || "",
  });
  const setPhones = (next) => patchData({
    phones: next,
    phone: next.map((x) => String(x).trim()).find(Boolean) || "",
  });
  const renderList = (label, items, setItems, testId, placeholder) => (
    <div>
      <div className="flex items-center justify-between gap-2">
        <Label>{label}</Label>
        <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-blue-600 hover:text-blue-700" data-testid={`${testId}-add`} onClick={() => setItems([...items, ""])}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add field
        </Button>
      </div>
      <div className="mt-1 space-y-2">
        {items.map((val, i) => (
          <div key={`${testId}-${i}`} className="flex gap-2">
            <Input data-testid={i === 0 ? testId : `${testId}-${i}`} className="flex-1" value={val} onChange={(e) => { const next = [...items]; next[i] = e.target.value; setItems(next); }} placeholder={placeholder} />
            {items.length > 1 ? (
              <button type="button" className="text-slate-400 hover:text-red-600 p-2 shrink-0" data-testid={`${testId}-remove-${i}`} onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Remove">
                <Trash2 className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>{t("pages.heading")}</Label><Input data-testid="contact-heading" className="mt-1" value={d.heading || ""} onChange={(e) => patchData("heading", e.target.value)} placeholder="Get in touch" /></div>
        <div><Label>{t("pages.subheading")}</Label><Input data-testid="contact-subheading" className="mt-1" value={d.subheading || ""} onChange={(e) => patchData("subheading", e.target.value)} placeholder="We'd love to hear from you" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {renderList(t("common.email"), emails, setEmails, "contact-email", "hello@yourstore.com")}
        {renderList(t("common.phone"), phones, setPhones, "contact-phone", "+91 90000 00000")}
        <div><Label>{t("common.whatsapp")}</Label><Input data-testid="contact-whatsapp" className="mt-1" value={d.whatsapp || ""} onChange={(e) => patchData("whatsapp", e.target.value)} placeholder="+91 90000 00000" /></div>
        <div><Label>{t("pages.businessHours")}</Label><Input data-testid="contact-hours" className="mt-1" value={d.hours || ""} onChange={(e) => patchData("hours", e.target.value)} placeholder="Mon–Sat, 9am–7pm" /></div>
      </div>
      <div><Label>{t("common.address")}</Label><Textarea rows={3} data-testid="contact-address" className="mt-1" value={d.address || ""} onChange={(e) => patchData("address", e.target.value)} placeholder="Shop 12, Main Road, City, State — 400001" /></div>
      <div><Label>{t("pages.mapEmbed")} <span className="text-slate-400 text-xs font-normal">({t("common.optional")})</span></Label><Input data-testid="contact-map" className="mt-1 font-mono-nums" value={d.map_embed || ""} onChange={(e) => patchData("map_embed", e.target.value)} placeholder="https://www.google.com/maps/embed?..." /></div>
    </div>
  );
}

function AccountDeletionEditor({ page, patchData }) {
  const { t } = useTranslation();
  const d = page.data || {};
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">{t("pages.accountDeletionHint")}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label>{t("pages.heading")}</Label><Input data-testid="acctdel-heading" className="mt-1" value={d.heading || ""} onChange={(e) => patchData("heading", e.target.value)} placeholder="Delete your account" /></div>
        <div><Label>{t("pages.subheading")}</Label><Input data-testid="acctdel-subheading" className="mt-1" value={d.subheading || ""} onChange={(e) => patchData("subheading", e.target.value)} placeholder="Request deletion of your store account" /></div>
      </div>
      <div><Label>{t("pages.accountDeletionInstructions")}</Label><Textarea rows={10} data-testid="acctdel-instructions" className="mt-1" value={d.instructions || ""} onChange={(e) => patchData("instructions", e.target.value)} placeholder="Explain how you handle deletion requests…" /></div>
      <div><Label>{t("pages.accountDeletionNote")}</Label><Input data-testid="acctdel-note" className="mt-1" value={d.processing_note || ""} onChange={(e) => patchData("processing_note", e.target.value)} placeholder="We typically complete requests within 7 days." /></div>
    </div>
  );
}

function AccountDeletionInbox() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const load = () => api.get("/merchant/account-deletion-requests").then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const mark = async (id, status) => {
    try {
      await api.patch(`/merchant/account-deletion-requests/${id}`, { status });
      load();
    } catch (e) { toast.error(extractErr(e)); }
  };
  return (
    <div className="pt-4 border-t border-slate-100 space-y-3" data-testid="acctdel-inbox">
      <div className="flex items-center justify-between">
        <Label>{t("pages.accountDeletionInbox")}</Label>
        <span className="text-xs text-slate-400">{items.length} {t("pages.accountDeletionCount")}</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-400">{t("pages.accountDeletionEmpty")}</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 p-3 text-sm" data-testid={`acctdel-req-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-slate-900">{r.name || "—"} · {r.email}</div>
                  {r.phone ? <div className="text-slate-500 text-xs mt-0.5">{r.phone}</div> : null}
                </div>
                <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${r.status === "done" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-800"}`}>{r.status || "new"}</span>
              </div>
              {r.reason ? <p className="mt-2 text-slate-600 whitespace-pre-wrap">{r.reason}</p> : null}
              <div className="mt-2 flex items-center justify-between text-xs text-slate-400">
                <span>{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</span>
                {r.status !== "done" ? (
                  <button type="button" className="text-blue-600 hover:text-blue-700 font-medium" data-testid={`acctdel-done-${r.id}`} onClick={() => mark(r.id, "done")}>{t("pages.accountDeletionMarkDone")}</button>
                ) : (
                  <button type="button" className="text-slate-500 hover:text-slate-700" onClick={() => mark(r.id, "new")}>{t("pages.accountDeletionReopen")}</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPages() {
  const { tenant } = useMerchantAuth();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const load = () => api.get("/merchant/pages").then(({ data }) => setPages(data));
  useEffect(() => { load(); }, []);

  const updateField = (slug, field, val) => setPages((prev) => prev.map((p) => (p.slug === slug ? { ...p, [field]: val } : p)));
  const patchData = (slug, keyOrObj, val) => setPages((prev) => prev.map((p) => {
    if (p.slug !== slug) return p;
    const extra = (keyOrObj && typeof keyOrObj === "object" && !Array.isArray(keyOrObj))
      ? keyOrObj
      : { [keyOrObj]: val };
    return { ...p, data: { ...(p.data || {}), ...extra } };
  }));

  const save = async (page) => {
    try {
      await api.put(`/merchant/pages/${page.slug}`, { title: page.title, content: page.content || "", data: page.data || {} });
      toast.success(t("common.saved"));
    } catch (e) { toast.error(extractErr(e)); }
  };

  const viewUrl = (slug) => storePageUrl(tenant, slug);

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">{t("pages.title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("pages.subtitle")}</p>
      </div>

      <Tabs defaultValue={searchParams.get("tab") || "about"}>
        <TabsList data-testid="pages-tabs" className="flex flex-wrap h-auto min-h-9 w-full justify-start">
          {pages.map((p) => (<TabsTrigger key={p.slug} value={p.slug} data-testid={`page-tab-${p.slug}`}>{PAGE_TAB[p.slug] ? t(PAGE_TAB[p.slug]) : p.title}</TabsTrigger>))}
        </TabsList>
        {pages.map((p) => (
          <TabsContent key={p.slug} value={p.slug}>
            <Card className="p-6 space-y-4 border-slate-200/70">
              <div className="flex items-center justify-between">
                <div><Label>{t("pages.menuTitle")}</Label><Input data-testid={`pg-title-${p.slug}`} className="mt-1 w-64" value={p.title} onChange={(e) => updateField(p.slug, "title", e.target.value)} /></div>
                <a href={viewUrl(p.slug)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid={`pg-view-${p.slug}`}>
                  <ExternalLink className="w-4 h-4" /> {t("pages.viewPage")}
                </a>
              </div>

              {p.slug === "about" ? (
                <AboutEditor page={p} patchData={(k, v) => patchData(p.slug, k, v)} />
              ) : p.slug === "contact" ? (
                <ContactEditor page={p} patchData={(k, v) => patchData(p.slug, k, v)} />
              ) : p.slug === "faq" ? (
                <FaqEditor page={p} patchData={(k, v) => patchData(p.slug, k, v)} />
              ) : p.slug === "account-deletion" ? (
                <>
                  <AccountDeletionEditor page={p} patchData={(k, v) => patchData(p.slug, k, v)} />
                  <AccountDeletionInbox />
                </>
              ) : (
                <div><Label>{t("pages.content")}</Label><Textarea rows={12} data-testid={`pg-content-${p.slug}`} className="mt-1" value={p.content || ""} onChange={(e) => updateField(p.slug, "content", e.target.value)} placeholder="Write your policy here…" /></div>
              )}

              <Button onClick={() => save(p)} data-testid={`pg-save-${p.slug}`} className="bg-blue-600 hover:bg-blue-700">{t("pages.savePage", { title: p.title })}</Button>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
